import React from 'react';
import { Badge, Button, Card, Empty, Modal, notification, Spin, Tooltip } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { OverViewCardInfo } from '../OverviewCardInfo';
import {
	DeleteOutline,
	EditOutlined,
	Error,
	UnfoldLess,
	UnfoldMore,
	DifferenceOutlined,
} from '@mui/icons-material';
import ApiWorkflowForm from './ApiWorkflowForm';
import classNames from 'classnames';
import { useState } from 'react';
import { CustomModal } from '@/components/CustomModal';
import { QueryObserverResult, RefetchOptions, UseMutationResult } from '@tanstack/react-query';
import { MODAL_IDS } from 'src/enums/EModal';
import './ApiWorkflowInfo.scss';
import { User } from '@/types/TAccounts';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { openModal, closeModal } from '@/store/modal/slice';
import { TUTORIALS } from 'src/enums/ETutorials';
import { API_WORKFLOW_TYPES } from 'src/constants';
import { ApiWorkflowErrorLogs } from './ApiWorkflowErrorLogs';
import { SetupWorkflowAvailability } from '../WorkflowAvailability/WorkflowAvailability';
import { TWorkflowAvailabilities } from '@/types/TWorkflowAvailabilities';
import { fetchAndUpdateWorkflowAnalysis } from '@/utils/analysisWorkflow';
import { FeatureFlag } from 'src/enums/EFeatureFlags';
import { useFeatureFlag } from 'src/Hooks/useFeatureFlag';
import { getSelectedAgentId } from '@/store/account/selector';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { decrementScenarioCount } from '@/store/scenarioCounts/slice';
import { WorkflowType } from 'src/enums/EWorkflow';
import { selectCanAddScenario } from '@/store/scenarioCounts/selector';
import { safeJSONParse } from '@/utils/helper';

export const ApiWorkflowInfo: React.FC<{
	data: any;
	refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<User, Error>>;
	mutation: UseMutationResult<unknown, Error, any, unknown>;
	debrand?: boolean;
}> = ({ data, refetch, mutation, debrand }) => {
	let apiWorkflowData: any = {};
	const isApiTokenIntegrationFlagEnabled = useFeatureFlag(FeatureFlag.API_TOKEN_INTEGRATION);
	const isAPiChainingFlagEnabled = useFeatureFlag(FeatureFlag.API_CHAINING);

	const parsedData = safeJSONParse(data?.call_webhook_workflows, []);

	if (Array.isArray(parsedData)) {
		apiWorkflowData = { call_webhook_workflows: parsedData };
	} else if (typeof parsedData === 'object') {
		apiWorkflowData = parsedData;
	} else {
		apiWorkflowData = { call_webhook_workflows: [] };
	}

	const dispatch = useAppDispatch();
	const [expandedItems, setExpandedItems] = useState<number[]>([]);
	const [updatingIndex, setUpdatingIndex] = useState<number | null>(null);
	const selectedAgentId = useAppSelector(getSelectedAgentId);
	const canAddScenario = useAppSelector(selectCanAddScenario);

	const toggleExpandCollapse = (index: number) => {
		setExpandedItems((items) =>
			items.includes(index) ? items.filter((itemIndex) => itemIndex !== index) : [...items, index],
		);
	};

	const [formInitialValues, setFormInitialValues] = useState<any>(null);
	const [editWorkflowIndex, setEditWorkflowIndex] = useState<number>(-1);
	const [duplicateWorkflow, setDuplicateWorkflow] = useState<any>(null);
	const [workFlowForErrorLogs, setWorkFlowForErrorLogs] = useState<{
		workflowName: string;
		forwardedFrom: string;
	}>({
		workflowName: '',
		forwardedFrom: '',
	});

	const showLogs = (workflowName: string) => {
		setWorkFlowForErrorLogs({ workflowName, forwardedFrom: data.number });
		dispatch(
			openModal({
				modalId: MODAL_IDS.API_WORKFLOW_ERROR_LOGS_MODAL,
			}),
		);
	};

	const handleEditScenario = (scenario: any, index: number) => {
		const initialValues = {
			call_webhook_workflows: [
				{
					...scenario,
					extractFromContext: scenario.extractFromContext || [],
				},
			],
		};
		setEditWorkflowIndex(index);
		setFormInitialValues(initialValues);
		dispatch(
			openModal({
				modalId: MODAL_IDS.EDIT_API_WORKFLOW_MODAL,
			}),
		);
	};

	const handleDuplicateScenario = (index: number) => {
		const workflowToDuplicate = {
			...apiWorkflowData.call_webhook_workflows[index],
			id: undefined,
		};
		setDuplicateWorkflow(workflowToDuplicate);
		dispatch(openModal({ modalId: MODAL_IDS.API_WORKFLOW_DUPLICATE_MODAL }));
	};

	const handleDuplicateModalClose = () => {
		setDuplicateWorkflow(null);
		dispatch(closeModal());
	};

	const handleDuplicateButtonClick = (index: number) => {
		if (!canAddScenario) {
			notification.error({
				message: 'Scenario limit reached',
				description: 'Max scenarios (100) reached. Please Delete existing to add more.',
			});
			return;
		}
		handleDuplicateScenario(index);
	};

	const handleDeleteScenario = async (index: number) => {
		Modal.confirm({
			title: 'Are you sure you want to delete this scenario?',
			content: `Scenario ${index + 1} will be permanently deleted.`,
			okButtonProps: { danger: true, 'data-testid': 'confirm-delete-apiworkflow-button' },
			okText: 'Delete',
			onOk: () => {
				try {
					// Retrieve the current form values
					const currentWorkflows = apiWorkflowData?.call_webhook_workflows || []; // Use apiWorkflowData instead of form.getFieldValue

					// Filter out the scenario that needs to be deleted based on the index
					const updatedWorkflows = currentWorkflows.filter((_: any, i: number) => i !== index);

					// Call mutation to update the backend with the updated workflows
					mutation.mutate(
						{
							call_webhook_workflows: JSON.stringify(updatedWorkflows),
							agentId: selectedAgentId as string,
						},
						{
							onSuccess: () => {
								dispatch(decrementScenarioCount(WorkflowType.WEBHOOK));

								notification.success({ message: `Scenario ${index + 1} deleted successfully.` });
								refetch();
							},
							onError: () => {
								notification.error({ message: 'Something went wrong, please try again later!' });
							},
						},
					);
				} catch (err) {
					console.log(err);
					notification.error({ message: 'Something went wrong, please try again later!' });
				}
			},
		});
		await fetchAndUpdateWorkflowAnalysis(dispatch, selectedAgentId as string);
	};

	const handleSetWorkflowAvailability = async (
		data: TWorkflowAvailabilities,
		index: number,
		fallbackMessage: string,
		configEnabled: boolean,
	) => {
		try {
			setUpdatingIndex(index);
			const currentWorkflows = apiWorkflowData?.call_webhook_workflows || [];
			const updatedWorkflows = [...currentWorkflows];

			if (updatedWorkflows[index]) {
				updatedWorkflows[index].availability = data;
				updatedWorkflows[index].configEnabled = configEnabled;
				updatedWorkflows[index].fallbackMessage = fallbackMessage;
			}
			if (!selectedAgentId) {
				setUpdatingIndex(null);
				notification.error({
					message: 'Error updating availability',
				});
				return;
			}
			await mutation.mutateAsync({
				call_webhook_workflows: JSON.stringify(updatedWorkflows),
				agentId: selectedAgentId,
			});
			notification.success({
				message: 'Workflow Availability has been updated!',
				duration: 3,
			});
			refetch();
		} catch (error: any) {
			notification.error({
				message: 'Error updating availability',
				description: error.toString(),
			});
		} finally {
			setUpdatingIndex(null);
		}
	};

	const InfoComponent = () => {
		return (
			<Spin
				spinning={mutation.isPending}
				indicator={
					<LoadingOutlined data-testid="main-loading-indicator" style={{ fontSize: '48px' }} spin />
				}
			>
				{apiWorkflowData && apiWorkflowData?.call_webhook_workflows?.length ? (
					apiWorkflowData?.call_webhook_workflows?.map((apiWorkflow: any, index: number) => (
						<Card
							key={index}
							className="mt-3 mb-3 workflow-card"
							title={
								<div className={`${baseClass}_button-container`}>
									<span className="font-bold text-base">{apiWorkflow.name}</span>

									<div className="action-buttons">
										<Tooltip title="Duplicate This Scenario">
											<Button
												type="default"
												icon={<DifferenceOutlined />}
												onClick={() => handleDuplicateButtonClick(index)}
											/>
										</Tooltip>
										<Button
											type="primary"
											danger
											icon={<DeleteOutline />}
											onClick={() => handleDeleteScenario(index)}
											data-testid={`delete-apiworkflow-button-${index}`}
										>
											<div>Delete</div>
										</Button>
										<Button
											type="primary"
											icon={<EditOutlined />}
											onClick={() => handleEditScenario(apiWorkflow, index)}
										>
											<div>Edit</div>
										</Button>
										<Button type="primary" onClick={() => showLogs(apiWorkflow.name)}>
											<div>Logs</div>
										</Button>
										<SetupWorkflowAvailability
											availability={apiWorkflow?.availability}
											initialConfigEnabled={apiWorkflow?.configEnabled ?? false}
											initialFallbackMessage={apiWorkflow?.fallbackMessage}
											setWorkflowAvailability={handleSetWorkflowAvailability}
											index={index}
											isLoading={updatingIndex === index}
										/>
										<Button
											type="primary"
											icon={expandedItems.includes(index) ? <UnfoldLess /> : <UnfoldMore />}
											onClick={() => toggleExpandCollapse(index)}
										>
											<div>{expandedItems.includes(index) ? 'Collapse' : 'Expand'}</div>
										</Button>
									</div>
								</div>
							}
						>
							{expandedItems.includes(index) ? (
								<div className="workflow-content">
									{/* Workflow Type Section */}
									<section className="workflow-section">
										<div className="section-header">
											<h3 className="section-title">Workflow Type</h3>
										</div>
										<div className="section-content">
											<div className="info-value break-words">
												{apiWorkflow.workflowType === 2
													? 'Knowledge Base Loader (Beginning of call)'
													: 'Scenario'}
											</div>
										</div>
									</section>

									{/* Knowledge Base Loader Prompt */}
									{apiWorkflow.workflowType === API_WORKFLOW_TYPES.knowledgeBaseLoader && (
										<section className="workflow-section">
											<div className="section-header">
												<h3 className="section-title">User Prompt</h3>
											</div>
											<div className="section-content">
												<div className="info-value">{apiWorkflow.prompt}</div>
											</div>
										</section>
									)}

									{/* Scenario Information */}
									{apiWorkflow.workflowType !== API_WORKFLOW_TYPES.knowledgeBaseLoader && (
										<section className="workflow-section">
											<div className="section-header">
												<h3 className="section-title">Scenario Information</h3>
											</div>
											<div className="section-content">
												<div className="info-value">{apiWorkflow.scenario}</div>
											</div>
											<div className="section-footer">
												<div className="flex justify-between items-center">
													<span className="text-[0.975rem] font-medium">Repeatable</span>
													<Badge className="badge-success" count="On" />
												</div>
											</div>
										</section>
									)}

									{/* Additional APIs Section */}
									{isAPiChainingFlagEnabled &&
										apiWorkflow.additionalApis &&
										apiWorkflow.additionalApis.length > 0 && (
											<section className="workflow-section">
												<div className="section-header">
													<h3 className="section-title">Chained APIs</h3>
													<div className="section-description">
														Additional APIs that will be called before the main API call.
													</div>
												</div>
												<div className="section-content">
													{apiWorkflow.additionalApis.map((api: any, apiIndex: number) => (
														<div key={apiIndex} className="subsection mb-4">
															<div className="subsection-header">
																<h4 className="text-blue-600 font-medium">API : {api.name}</h4>
															</div>
															<div className="info-item">
																<span className="info-label">URL:</span>
																<span className="info-value font-mono text-sm break-all">
																	{api.url}
																</span>
															</div>
															<div className="flex justify-between items-center mb-3">
																<span className="text-gray-600">Url Encoded</span>
																<Badge
																	className={classNames({
																		'badge-success': !!api.isUrlEncoded,
																		'badge-error': !api.isUrlEncoded,
																	})}
																	count={api.isUrlEncoded ? 'On' : 'Off'}
																/>
															</div>
															{/* Conditional Execution Display */}
															{api.hasCondition && api.condition && (
																<div className="mb-2 p-2 bg-yellow-50 border-l-4 border-yellow-400 rounded">
																	<div className="font-semibold text-yellow-700 mb-1">
																		Conditional Execution
																	</div>
																	<div className="text-sm text-gray-700">
																		<span>If </span>
																		<span className="font-mono bg-gray-100 px-1 rounded">{`API ${api.condition.sourceApiIndex + 1}`}</span>
																		<span>'s </span>
																		<span className="font-mono bg-gray-100 px-1 rounded">
																			{api.condition.variable}
																		</span>
																		<span>
																			{' '}
																			{api.condition.operator
																				.replace('equals', '==')
																				.replace('notEquals', '!=')
																				.replace('greaterThan', '>')
																				.replace('lessThan', '<')
																				.replace('contains', 'contains')
																				.replace('notContains', 'does not contain')
																				.replace('exists', 'exists')
																				.replace('notExists', 'does not exist')}
																		</span>
																		{typeof api.condition.value !== 'undefined' &&
																			api.condition.value !== '' && (
																				<span>
																					{' '}
																					<span className="font-mono bg-gray-100 px-1 rounded">
																						{api.condition.value}
																					</span>
																				</span>
																			)}
																	</div>
																</div>
															)}
															{/* Convert Response Data to table format */}
															{api.responseData && api.responseData.length > 0 && (
																<div className="mt-3">
																	<div className="info-label mb-2">Response Data:</div>
																	<div className="overflow-x-auto -mx-2 sm:mx-0">
																		<div className="border border-gray-200 rounded-lg overflow-hidden bg-white min-w-full">
																			<table className="w-full">
																				<thead className="bg-gray-50">
																					<tr>
																						<th className="px-2 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider md:w-1/5 w-1/3">
																							Variable
																						</th>
																						<th className="px-2 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider md:w-1/5 w-1/3">
																							JSON Path
																						</th>
																					</tr>
																				</thead>
																				<tbody className="divide-y divide-gray-200">
																					{api?.responseData.map(
																						(response: any, responseIndex: number) => (
																							<tr key={responseIndex}>
																								<td className="px-4 py-2 text-sm font-medium text-blue-600 break-words">
																									{response?.variableName}
																								</td>
																								<td className="px-4 py-2 text-sm font-mono break-words">
																									{response?.jsonPath}
																								</td>
																							</tr>
																						),
																					)}
																				</tbody>
																			</table>
																		</div>
																	</div>
																</div>
															)}
														</div>
													))}
												</div>
											</section>
										)}

									{/* Extracted Call Information Section */}
									{apiWorkflow.workflowType !== 2 &&
										apiWorkflow.extractedCallInfo &&
										apiWorkflow.extractedCallInfo.length > 0 && (
											<section className="workflow-section">
												<div className="section-header">
													<h3 className="section-title">Extract Call Information</h3>
													<div className="section-description">
														Information will be extracted from the call into variables and these
														variables will be used in your webhook parameters.
													</div>
												</div>
												<div className="section-content">
													<div className="flex justify-between items-center mb-3">
														<span className="text-gray-600">Query Params</span>
														<Badge
															className={classNames({
																'badge-success': !!apiWorkflow.isQueryParams,
																'badge-error': !apiWorkflow.isQueryParams,
															})}
															count={apiWorkflow.isQueryParams ? 'On' : 'Off'}
														/>
													</div>

													{/* Convert to table format */}
													<div className="border border-gray-200 rounded-lg overflow-hidden bg-white mb-4">
														<table className="w-full">
															<thead className="bg-gray-50">
																<tr>
																	<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
																		Variable Name
																	</th>
																	<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">
																		Type
																	</th>
																	<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
																		Description
																	</th>
																</tr>
															</thead>
															<tbody className="divide-y divide-gray-200">
																{apiWorkflow.extractedCallInfo.map(
																	(info: any, infoIndex: number) => (
																		<tr key={infoIndex}>
																			<td className="px-4 py-2 text-sm font-medium text-blue-600">
																				{info.name}
																			</td>
																			<td className="px-4 py-2 text-sm">{info.type}</td>
																			<td className="px-4 py-2 text-sm text-gray-700">
																				{info.description}
																			</td>
																		</tr>
																	),
																)}
															</tbody>
														</table>
													</div>
												</div>
											</section>
										)}

									{/* Extract from Previous Context */}
									{apiWorkflow.workflowType !== API_WORKFLOW_TYPES.knowledgeBaseLoader &&
										apiWorkflow.extractFromContext &&
										apiWorkflow.extractFromContext.length > 0 && (
											<section className="workflow-section">
												<div className="section-header">
													<h3 className="section-title" data-testid="context-extraction-title">
														Use Previous Information
													</h3>
													<div className="section-description">
														Variables extracted from earlier in the conversation.
													</div>
												</div>
												<div className="section-content">
													<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
														{apiWorkflow.extractFromContext.map(
															(contextVar: any, contextIndex: number) => (
																<div
																	key={contextIndex}
																	className="info-card"
																	data-testid="context-extraction-item"
																>
																	<div className="mb-1">
																		<span className="text-gray-600 text-sm">Variable Name: </span>
																		<span
																			className="text-blue-600 font-medium"
																			data-testid="variable-name"
																		>
																			{contextVar.name}
																		</span>
																	</div>
																	<div className="text-sm">
																		<span className="text-gray-600">Extraction Method: </span>
																		<span data-testid="extraction-prompt">
																			{contextVar.extractionPrompt}
																		</span>
																	</div>
																</div>
															),
														)}
													</div>
												</div>
											</section>
										)}

									{/* Extract from system variable */}
									{apiWorkflow.workflowType !== API_WORKFLOW_TYPES.knowledgeBaseLoader &&
										apiWorkflow.systemVariable &&
										apiWorkflow.systemVariable.length > 0 && (
											<section className="workflow-section">
												<div className="section-header">
													<h3 className="section-title">Use System Information</h3>
													<div className="section-description">
														Variables extracted from system.
													</div>
												</div>
												<div className="section-content">
													<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
														{apiWorkflow.systemVariable.map(
															(contextVar: any, contextIndex: number) => (
																<div key={contextIndex} className="info-card">
																	<div className="mb-1">
																		<span className="text-gray-600 text-sm">Variable Name: </span>
																		<span
																			className="text-blue-600 font-medium"
																			data-testid="variable-name"
																		>
																			{contextVar.name}
																		</span>
																	</div>
																	<div className="text-sm">
																		<span className="text-gray-600">System Value: </span>
																		<span className="font-mono" data-testid="extraction-prompt">
																			Extraction value: {contextVar.type}
																		</span>
																	</div>
																</div>
															),
														)}
													</div>
												</div>
											</section>
										)}

									{/* Webhook Details */}
									<section className="workflow-section">
										<div className="section-header">
											<h3 className="section-title">Webhook</h3>
											<div className="section-description">
												Configure the webhook URL and fields like headers, method, body, and
												response data information.
											</div>
										</div>
										<div className="section-content">
											<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
												<div className="info-card">
													<div className="text-gray-600 text-sm mb-1">HTTP Method:</div>
													<div className="text-blue-600 font-medium">
														{apiWorkflow.webhookInformation.method}
													</div>
												</div>
												<div className="info-card">
													<div className="text-gray-600 text-sm mb-1">URL:</div>
													<div className="font-mono text-sm break-all">
														{apiWorkflow.webhookInformation.url}
													</div>
												</div>
											</div>

											{/* Headers Section */}
											<div className="subsection mb-4">
												<div className="flex justify-between items-center mb-3">
													<h4 className="subsection-title">Headers</h4>
													<Badge
														className={classNames({
															'badge-success': !!apiWorkflow.webhookInformation?.headers?.length,
															'badge-error': !apiWorkflow.webhookInformation?.headers?.length,
														})}
														count={apiWorkflow.webhookInformation?.headers?.length ? 'On' : 'Off'}
													/>
												</div>
												{apiWorkflow.webhookInformation?.headers &&
												apiWorkflow.webhookInformation?.headers.length > 0 ? (
													<div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
														<table className="w-full">
															<thead className="bg-gray-50">
																<tr>
																	<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
																		Header Name
																	</th>
																	<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
																		Value
																	</th>
																</tr>
															</thead>
															<tbody className="divide-y divide-gray-200">
																{apiWorkflow.webhookInformation.headers.map(
																	(header: any, headerIndex: number) => (
																		<tr key={headerIndex}>
																			<td className="px-4 py-2 text-sm font-medium text-blue-600">
																				{header.key}
																			</td>
																			<td className="px-4 py-2 text-sm text-gray-700">
																				{header.value}
																			</td>
																		</tr>
																	),
																)}
															</tbody>
														</table>
													</div>
												) : (
													<div className="text-gray-400">No Headers</div>
												)}
											</div>

											{apiWorkflow.workflowType !== 2 && (
												<>
													<div className="flex justify-between items-center mb-3">
														<span className="text-gray-600">Request URL Encoded</span>
														<Badge
															className={classNames({
																'badge-success': !!apiWorkflow.isURLEncodedApi,
																'badge-error': !apiWorkflow.isURLEncodedApi,
															})}
															count={apiWorkflow.isURLEncodedApi ? 'On' : 'Off'}
														/>
													</div>

													{/* Request Body Section */}
													<div className="subsection mb-4">
														<div className="flex justify-between items-center mb-3">
															<h4 className="subsection-title">Request Body</h4>
															<Badge
																className={classNames({
																	'badge-success': !!apiWorkflow.webhookInformation?.body,
																	'badge-error': !apiWorkflow.webhookInformation?.body,
																})}
																count={apiWorkflow.webhookInformation?.body ? 'On' : 'Off'}
															/>
														</div>
														{apiWorkflow.webhookInformation.body ? (
															<div className="code-block">
																<pre className="text-sm overflow-x-auto whitespace-pre-wrap">
																	{JSON.stringify(apiWorkflow.webhookInformation.body, null, 2)}
																</pre>
															</div>
														) : (
															<div className="text-gray-400">No Request Body</div>
														)}
													</div>

													{/* Response Data Information */}
													<div className="subsection mb-4">
														<div className="flex justify-between items-center mb-3">
															<h4 className="subsection-title">Response Data Information</h4>
															<Badge
																className={classNames({
																	'badge-success':
																		!!apiWorkflow.webhookInformation?.responseData?.length,
																	'badge-error':
																		!apiWorkflow.webhookInformation?.responseData?.length,
																})}
																count={
																	apiWorkflow.webhookInformation?.responseData?.length
																		? 'On'
																		: 'Off'
																}
															/>
														</div>
														{apiWorkflow.webhookInformation.responseData &&
														apiWorkflow.webhookInformation.responseData.length > 0 ? (
															<div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
																<table className="w-full">
																	<thead className="bg-gray-50">
																		<tr>
																			<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">
																				Variable
																			</th>
																			<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">
																				JSON Path
																			</th>
																			<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
																				Prompt
																			</th>
																		</tr>
																	</thead>
																	<tbody className="divide-y divide-gray-200">
																		{apiWorkflow?.webhookInformation?.responseData?.map(
																			(response: any, responseIndex: number) => (
																				<tr key={responseIndex}>
																					<td className="px-4 py-2 text-sm font-medium text-blue-600">
																						{response?.variableName}
																					</td>
																					<td className="px-4 py-2 text-sm font-mono">
																						{response?.jsonPath}
																					</td>
																					<td className="px-4 py-2 text-sm text-gray-700">
																						{response?.prompt}
																					</td>
																				</tr>
																			),
																		)}
																	</tbody>
																</table>
															</div>
														) : (
															<div className="text-gray-400">No Response Data</div>
														)}
													</div>
												</>
											)}

											{/* Timeout Duration */}
											<div className="info-card flex justify-between items-center">
												<span className="text-gray-600 font-medium">Timeout Duration:</span>
												<span className="text-blue-600">{apiWorkflow.timeoutSeconds} seconds</span>
											</div>
										</div>
									</section>

									{/* Speech to Emit */}
									{apiWorkflow.workflowType !== API_WORKFLOW_TYPES.knowledgeBaseLoader && (
										<section className="workflow-section">
											<div className="section-header">
												<h3 className="section-title">Speech to Emit</h3>
											</div>
											<div className="section-content">
												<div className="info-card">
													<div className="text-sm">
														{apiWorkflow?.speechToEmit || 'None specified'}
													</div>
												</div>
											</div>
										</section>
									)}

									{/*Access Token Configuration Section */}
									{isApiTokenIntegrationFlagEnabled ? (
										<section className="workflow-section">
											<div className="section-header">
												<h3 className="section-title">Access Token Configuration</h3>
												<div className="section-description">
													Configuration for dynamic token retrieval
												</div>
											</div>
											<div className="section-content">
												<div className="flex justify-between items-center mb-3">
													<span className="text-gray-600">Requires Auth Token</span>
													<Badge
														className={classNames({
															'badge-success': !!apiWorkflow.requiresAuthToken,
															'badge-error': !apiWorkflow.requiresAuthToken,
														})}
														count={apiWorkflow.requiresAuthToken ? 'On' : 'Off'}
													/>
												</div>

												{apiWorkflow.requiresAuthToken && (
													<div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
														<table className="w-full">
															<thead className="bg-gray-50">
																<tr>
																	<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">
																		Property
																	</th>
																	<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
																		Value
																	</th>
																</tr>
															</thead>
															<tbody className="divide-y divide-gray-200">
																<tr>
																	<td className="px-4 py-2 text-sm font-medium text-gray-600">
																		Endpoint
																	</td>
																	<td className="px-4 py-2 text-sm break-all">
																		{apiWorkflow.authApi.url}
																	</td>
																</tr>
																<tr>
																	<td className="px-4 py-2 text-sm font-medium text-gray-600">
																		URL-encoded
																	</td>
																	<td className="px-4 py-2 text-sm">
																		<Badge
																			className={classNames({
																				'badge-success': !!apiWorkflow.authApi.isUrlEncoded,
																				'badge-error': !apiWorkflow.authApi.isUrlEncoded,
																			})}
																			count={apiWorkflow.authApi.isUrlEncoded ? 'On' : 'Off'}
																		/>
																	</td>
																</tr>
																<tr>
																	<td className="px-4 py-2 text-sm font-medium text-gray-600">
																		Method
																	</td>
																	<td className="px-4 py-2 text-sm">
																		{apiWorkflow.authApi.method}
																	</td>
																</tr>
																<tr>
																	<td className="px-4 py-2 text-sm font-medium text-gray-600">
																		Token Variable
																	</td>
																	<td className="px-4 py-2 text-sm">
																		{apiWorkflow.authApi.tokenVariable}
																	</td>
																</tr>
																<tr>
																	<td className="px-4 py-2 text-sm font-medium text-gray-600">
																		Token Path
																	</td>
																	<td className="px-4 py-2 text-sm">
																		{apiWorkflow.authApi.tokenPath}
																	</td>
																</tr>
															</tbody>
														</table>
													</div>
												)}
											</div>
										</section>
									) : (
										''
									)}

									{/* Post-Execution Prompt */}
									{apiWorkflow.postExecutionPrompt && (
										<section className="workflow-section">
											<div className="section-header">
												<h3 className="section-title">What should the AI do next</h3>
											</div>
											<div className="section-content">
												<div className="info-card">
													<div className="text-sm">{apiWorkflow?.postExecutionPrompt}</div>
												</div>
											</div>
										</section>
									)}
								</div>
							) : (
								<div className="flex justify-center items-center py-2">
									<span className="text-base text-gray-400">Expand To View Details</span>
								</div>
							)}
						</Card>
					))
				) : (
					<Empty
						image={'/empty.png'}
						imageStyle={{ justifyItems: 'center' }}
						description="No Workflow Found"
					/>
				)}
			</Spin>
		);
	};

	const baseClass = 'api-workflow';

	const codeIcon = (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			className="h-5 w-5"
			viewBox="0 0 20 20"
			fill="currentColor"
		>
			<path
				fillRule="evenodd"
				d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z"
				clipRule="evenodd"
			/>
		</svg>
	);

	return (
		<div className={baseClass}>
			<OverViewCardInfo
				width={'50%'}
				formActionType="Add"
				form={<ApiWorkflowForm apiWorkflowData={apiWorkflowData} refetch={refetch} />}
				formTitle="Add Workflow"
				info={<InfoComponent />}
				footer={null}
				modalId={MODAL_IDS.API_WORKFLOW_MODAL}
				tutorialId={TUTORIALS.API_WORKFLOW}
				displayTutorialActionBtn={!debrand}
				icon={codeIcon}
			/>

			<CustomModal
				width={'50%'}
				title={<h1 className="text-xl font-semibold text-gray-800">Edit Scenario</h1>}
				children={
					<ApiWorkflowForm
						apiWorkflowData={apiWorkflowData}
						showAddFieldButton={false}
						call_webhook_workflows={formInitialValues}
						editWorkflowIndex={editWorkflowIndex}
						refetch={refetch}
					/>
				}
				footer={null}
				modalId={MODAL_IDS.EDIT_API_WORKFLOW_MODAL}
				icon={codeIcon}
			/>

			<CustomModal
				width={'50%'}
				title={<h1 className="text-xl font-semibold text-gray-800">API Workflow Logs</h1>}
				children={<ApiWorkflowErrorLogs workFlowForErrorLogs={workFlowForErrorLogs} />}
				footer={null}
				modalId={MODAL_IDS.API_WORKFLOW_ERROR_LOGS_MODAL}
			/>

			<CustomModal
				width={'50%'}
				title={<h1 className="text-xl font-semibold text-gray-800">Duplicate Scenario</h1>}
				children={
					<ApiWorkflowForm
						apiWorkflowData={apiWorkflowData}
						refetch={refetch}
						call_webhook_workflows={
							duplicateWorkflow ? { call_webhook_workflows: [duplicateWorkflow] } : undefined
						}
						isDuplicate={true}
						showAddFieldButton={false}
					/>
				}
				footer={null}
				modalId={MODAL_IDS.API_WORKFLOW_DUPLICATE_MODAL}
				cancelAction={handleDuplicateModalClose}
				destroyOnClose={true}
				icon={codeIcon}
			/>
		</div>
	);
};
